/** Maps card-create SP FieldKeys → Add Card form fields (request-first holder placeholders). */

export type HolderFieldMeta = {
    /** PascalCase name as used in API metadata */
    name: string;
    label: string;
    type: string;
    bRequired?: boolean;
};

/** Backend FieldKey (exact) → form metadata to inject when missing. */
export const CARD_CREATE_REQUIRED_FIELD_MAP: Record<string, HolderFieldMeta> = {
    'TaxID/SSN': {
        name: 'TaxNumber',
        label: 'Tax ID / SSN',
        type: 'String',
        bRequired: true,
    },
    dateOfBirth: {
        name: 'DateOfBirth',
        label: 'Date of Birth',
        type: 'DateTime',
        bRequired: true,
    },
    firstName: {
        name: 'FirstName',
        label: 'First Name',
        type: 'String',
        bRequired: true,
    },
    lastName: {
        name: 'LastName',
        label: 'Last Name',
        type: 'String',
        bRequired: true,
    },
    phoneNumber: {
        name: 'PhoneNumber',
        label: 'Phone Number',
        type: 'String',
        bRequired: true,
    },
    emailAddress: {
        name: 'EmailAddress',
        label: 'Email Address',
        type: 'String',
        bRequired: true,
    },
    legalEntityName: {
        name: 'LegalEntityName',
        label: 'Legal Entity Name',
        type: 'String',
        bRequired: true,
    },
    dateOfIncorporation: {
        name: 'DateOfIncorporation',
        label: 'Date of Incorporation',
        type: 'DateTime',
        bRequired: true,
    },
    registeredAddressStreet: {
        name: 'RegisteredAddressStreet',
        label: 'Registered Address Street',
        type: 'String',
        bRequired: true,
    },
    registeredAddressCity: {
        name: 'RegisteredAddressCity',
        label: 'Registered Address City',
        type: 'String',
        bRequired: true,
    },
    registeredAddressState: {
        name: 'RegisteredAddressState',
        label: 'Registered Address State',
        type: 'String',
        bRequired: true,
    },
    registeredAddressPostcode: {
        name: 'RegisteredAddressPostcode',
        label: 'Registered Address Postcode',
        type: 'String',
        bRequired: true,
    },
    registeredAddressCountry: {
        name: 'RegisteredAddressCountry',
        label: 'Registered Address Country',
        type: 'String',
        bRequired: true,
    },
    'ShippingAddress.ShippingAddressLine1': {
        name: 'ShippingAddressLine1',
        label: 'Shipping Address Line 1',
        type: 'String',
        bRequired: true,
    },
    'ShippingAddress.ShippingAddressCity': {
        name: 'ShippingAddressCity',
        label: 'Shipping Address City',
        type: 'String',
        bRequired: true,
    },
    'ShippingAddress.ShippingAddressState': {
        name: 'ShippingAddressState',
        label: 'Shipping Address State',
        type: 'String',
        bRequired: true,
    },
    'ShippingAddress.ShippingAddressPostcode': {
        name: 'ShippingAddressPostcode',
        label: 'Shipping Address Postcode',
        type: 'String',
        bRequired: true,
    },
};

export type ApiFieldError = { fieldName: string; messageCode: string; messageText?: string };

export type MappedCardCreateFieldError = {
    formFieldName: string;
    message: string;
    meta: HolderFieldMeta;
};

const toFormFieldName = (pascalOrCamel: string): string =>
    pascalOrCamel.charAt(0).toLowerCase() + pascalOrCamel.slice(1);

/**
 * Map API card-create validation errors to form field names + metadata for placeholders.
 * Only FieldKeys in CARD_CREATE_REQUIRED_FIELD_MAP are handled (holder / address / shipping).
 */
export function mapCardCreateRequiredFieldErrors(
    errors: ApiFieldError[]
): MappedCardCreateFieldError[] {
    const mapped: MappedCardCreateFieldError[] = [];
    const seen = new Set<string>();

    for (const error of errors || []) {
        const key = (error.fieldName || '').trim();
        const meta = CARD_CREATE_REQUIRED_FIELD_MAP[key];
        if (!meta) continue;
        if ((error.messageCode || '').toUpperCase() !== 'REQUIRED') continue;

        const formFieldName = toFormFieldName(meta.name);
        if (seen.has(formFieldName)) continue;
        seen.add(formFieldName);

        mapped.push({
            formFieldName,
            message: error.messageText || error.messageCode || 'Required',
            meta,
        });
    }

    return mapped;
}
